// SLOT WORLD — groundref's inputs for ONE GmTex slot, windowed to the served files it reads
// (slotgeom slotReads): the Build's pre-paint (engine/steps/paint_slot, APP_PREPAINT) and the
// probe's ground check (tools/gpuprobe.mjs) paint a slot from the bytes the device painter read,
// in memory that scales with the slot. Pure: the files come in decoded (the caller's decoders —
// surface exactPlanes for a PNG, a WebP decoder, groundcpu nodeCpu, crownpack decodeCrowns).
//
//   worldBase(M, d) -> B    the world's constant terms, once. M = meta.json; d = {
//       genome (genome.json), water (water.json), nodeAt(j_tx_ty) (the ground tree has the node), pal (painterpal.json), gtTable (genome_noise.json),
//       look (wayslook.json look), marks (groundgen MARKS_IN), strata, tiling (ground's painted params),
//       residual [{tx, ty, w, h, data ch 3}] | null, s2col {w, h, data ch 3} | null,
//       rockfield {w, h, data ch 3} | null, exemplars {key: {w, h, data ch 3}},
//       coarse {w, h, data} | null (surface.coarse: the levels past WAYS_MAX_J read it) }
//     B = {M, geo (geomOf's world), W0 (groundref prepWorld of the constants), nodeAt, crIx}
//   readsOf(B, key) -> {G, reads}   geomOf + slotReads of slot j_tx_ty
//   slotWorld(B, G, got) -> a groundref world of the slot; got: Map id -> the decoded read
//       (img: {w, h, data} as exactPlanes / the WebP decoder give it for its ch; node: nodeCpu's
//       {mesh}; cpk: decodeCrowns) — a read absent from got is an absent file (the device's 404); the levels past
//       WAYS_MAX_J paint the ways over the 4 m mass (q: tiles, ch 4: Q4.TILE x 3 Q4.TILE RGBA, WAYS-Q4)
import { prepWorld, slotPart, strataField } from './groundref.js';
import { prepWays, prepQ4, q4FromPlanes, Q4 } from './groundways.js';
import { geomOf, slotReads, strataSum, sunOf } from './slotgeom.js';

// surfacegen ANCHOR_DEFAULT / GRASS_SAT: the painter's values when the bake's meta.tex.s2 is null (the
// Python constants; no JSON carries them; groundgen's too)
export const ANCHOR_DEFAULT = 2.96, GRASS_SAT_DEFAULT = 2.0;

// the sheet lakes (WATER-G beds) of water.json: polygons (+ holes) and their bounding boxes
export function sheetsOf(water) {
  return ((water && water.lakes) || []).filter((l) => l.sheet && l.polygon && l.polygon.length >= 3).map((l) => {
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (const p of l.polygon) { if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0]; if (p[1] < z0) z0 = p[1]; if (p[1] > z1) z1 = p[1]; }
    return { rings: [l.polygon, ...(l.holes || [])], bb: [x0, z0, x1, z1] };
  });
}
// even-odd point-in-polygon over a lake's rings ([[x, z], ...] each)
export function inRings(rings, x, z) {
  let inside = false;
  for (const R of rings) {
    for (let i = 0, j = R.length - 1; i < R.length; j = i++) {
      const [xi, zi] = R[i], [xj, zj] = R[j];
      if ((zi > z) !== (zj > z) && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
    }
  }
  return inside;
}

export function worldBase(M, d) {
  const s = M.surface, bb = M.bbox, ids = {};
  for (const c of s.classes || []) ids[c.key] = c.id;
  const waterId = s.water_id, noneId = s.none_id, L = M.tex.light, lg = M.lighting;
  // the residual grid (C5) whole, from its tiles (row 0 north; 128 where no tile decoded)
  const R = s.residual;
  let residual = { codes: new Uint8Array([128, 128, 128]), w: 1, h: 1, cell: 1e9, x0: 0, z0: 0 };
  if (R && d.residual && d.residual.length) {
    const codes = new Uint8Array(R.w * R.h * 3).fill(128), px = R.tile_px;
    for (const t of d.residual) {
      for (let r = 0; r < t.h; r++) for (let i = 0; i < t.w; i++) {
        const X = t.tx * px + i, Z = t.ty * px + r;
        if (X < R.w && Z < R.h) for (let c = 0; c < 3; c++) codes[3 * (Z * R.w + X) + c] = t.data[3 * (r * t.w + i) + c];
      }
    }
    residual = { codes, w: R.w, h: R.h, cell: R.cell_m, x0: R.origin[0] - bb.e0, z0: bb.n1 - R.origin[1] };
  }
  const S2 = s.s2col;
  const s2col = S2 && d.s2col ? { img: d.s2col.data, w: d.s2col.w, h: d.s2col.h, cell: S2.cell_m, x0: S2.origin[0] - bb.e0, z0: bb.n1 - S2.origin[1] } : null;
  const RF = s.rockfield;
  const rockfield = RF && d.rockfield && d.rockfield.w === RF.w && d.rockfield.h === RF.h ? { meta: RF, img: d.rockfield.data } : null;
  const recs = d.genome.classes || {}, exemplars = {};
  for (const [k, e] of Object.entries(d.exemplars || {})) {
    const E = recs[k] && recs[k].exemplar;
    if (E && e.w === E.px && e.h === E.px) exemplars[k] = { img: e.data, n: e.w, pxM: E.px_m };
  }
  const bands = M.bands, light = { sky: L.sky, ground: L.ground, sun: L.sun, white: L.white, sunAltDeg: lg.sun_alt_deg, sunAzDeg: lg.sun_az_deg };
  const crownC = { species: M.vegetation && M.vegetation.species, appearHist: (M.trees && M.trees.appear_hist) || [], treelineM: bands.treeline_m };
  const W0 = prepWorld({
    w: s.w, h: s.h, cm: new Uint8Array(0), cmWin: { x0: 0, z0: 0, w: 0, h: 0 }, dtm: null, ids, waterId, noneId, bands, pal: d.pal,
    genome: d.genome, gtTable: d.gtTable, residual, s2col,
    anchor: (M.tex.s2 && M.tex.s2.anchor) ?? ANCHOR_DEFAULT,
    grassSat: (M.region && M.region.texture && M.region.texture.grass_sat) ?? (M.tex.s2 && M.tex.s2.grass_sat) ?? GRASS_SAT_DEFAULT,
    sheet: null, ways: null, ground: new Map(), shd2: { vis: [], sky: [] }, crowns: { trees: [], shrubs: [], ...crownC },
    light, rockfield, strata: d.strata, exemplars, tiling: d.tiling, look: d.look,
  });
  // geomOf's world (groundgen _prepWorld's terms it reads)
  const sh = s.shade.shd2, lv = (a) => a.map((l) => ({ cell: l.cell_m, w: l.w, h: l.h, nx: l.nx, ny: l.ny })).sort((a2, b2) => a2.cell - b2.cell);
  const strata = rockfield ? strataField(RF, rockfield.img, ids) : null;
  if (strata) strata.sum = strataSum(strata);
  const geo = { W: s.w, H: s.h, sun: sunOf(lg.sun_az_deg, lg.sun_alt_deg), wayP: s.ways, strata,
    shd2: { dir: sh.dir, ext: sh.ext, px: sh.tile_px, vis: lv(sh.vis), sky: lv(sh.sky) } };
  const crS = new Map();
  return { M, d, ids, noneId, bands, geo, W0, sheets: sheetsOf(d.water), coarse: d.coarse, nodeAt: d.nodeAt,
    crIx: (D) => crS.get(D) || crS.set(D, new Set(D.list.map(([i, jj]) => i * 65536 + jj))).get(D) };
}

export function readsOf(B, key) {
  const G = geomOf(key, B.geo, B.M.surface.coarse.stride);
  return { G, reads: slotReads(G, B.geo, B.M, B.nodeAt, B.crIx) };
}

export function slotWorld(B, G, got) {
  const M = B.M, s = M.surface, c = G.cls, noneId = B.noneId;
  // the class window (+ the sheet beds, 1 m levels): the 1 m tiles, else the coarse grid expanded to
  // 1 m cells (the GPU's class at a cell of a no-ways level: coarse[floor(x / S)], clamped)
  let cm, cmWin, sheet = null;
  if (G.fine1) {
    const tm = s.tiles.tile_m;
    cmWin = { x0: c.x0, z0: c.z0, w: c.w, h: c.h };
    cm = new Uint8Array(c.w * c.h).fill(noneId);
    for (let ty = Math.floor(c.z0 / tm); ty <= Math.floor((c.z0 + c.h - 1) / tm); ty++) for (let tx = Math.floor(c.x0 / tm); tx <= Math.floor((c.x0 + c.w - 1) / tm); tx++) {
      const t = got.get(`c:${tx}_${ty}`);
      if (!t) continue;
      const i0 = Math.max(c.x0, tx * tm), i1 = Math.min(c.x0 + c.w, (tx + 1) * tm);
      for (let Z = Math.max(c.z0, ty * tm); Z < Math.min(c.z0 + c.h, (ty + 1) * tm); Z++) {
        const o = (Z - ty * tm) * tm - tx * tm;
        cm.set(t.data.subarray(o + i0, o + i1), (Z - c.z0) * c.w + i0 - c.x0);
      }
    }
    sheet = new Uint8Array(c.w * c.h);
    for (const L of B.sheets) {
      const [bx0, bz0, bx1, bz1] = L.bb;
      for (let Z = Math.max(c.z0, Math.floor(bz0)); Z <= Math.min(c.z0 + c.h - 1, Math.ceil(bz1)); Z++) {
        for (let X = Math.max(c.x0, Math.floor(bx0)); X <= Math.min(c.x0 + c.w - 1, Math.ceil(bx1)); X++) if (inRings(L.rings, X + 0.5, Z + 0.5)) sheet[(Z - c.z0) * c.w + X - c.x0] = 1;
      }
    }
  } else {
    const f = B.coarse, S = c.S;
    if (!f) throw new Error('slotworld: a no-ways level reads surface.coarse');
    cmWin = { x0: c.x0 * S, z0: c.z0 * S, w: c.w * S, h: c.h * S };
    cm = new Uint8Array(cmWin.w * cmWin.h);
    for (let k = 0; k < cmWin.h; k++) {
      const Z = Math.min(f.h - 1, Math.max(0, Math.floor((cmWin.z0 + k) / S))), o = k * cmWin.w, r = Z * f.w;
      for (let i = 0; i < cmWin.w; i++) { const X = Math.floor((cmWin.x0 + i) / S); cm[o + i] = f.data[r + (X < 0 ? 0 : X >= f.w ? f.w - 1 : X)]; }
    }
  }
  // the ways field window (8 bytes a cell: the two RGBA planes), prepWays on it
  let ways = null;
  if (G.ways) {
    const w = G.ways, P = s.ways, wt = P.tile_m, field = new Uint8Array(w.w * w.h * 8), n = wt * wt;
    for (let q = 0; q < w.w * w.h; q++) field.set(P.empty, 8 * q);
    for (let ty = Math.floor(w.z0 / wt); ty <= Math.floor((w.z0 + w.h - 1) / wt); ty++) for (let tx = Math.floor(w.x0 / wt); tx <= Math.floor((w.x0 + w.w - 1) / wt); tx++) {
      const t = got.get(`w:${tx}_${ty}`);
      if (!t) continue;
      for (let Z = Math.max(w.z0, ty * wt); Z < Math.min(w.z0 + w.h, (ty + 1) * wt); Z++) for (let X = Math.max(w.x0, tx * wt); X < Math.min(w.x0 + w.w, (tx + 1) * wt); X++) {
        const q = (Z - ty * wt) * wt + X - tx * wt, o = 8 * ((Z - w.z0) * w.w + X - w.x0);
        for (let ch = 0; ch < 4; ch++) { field[o + ch] = t.data[4 * q + ch]; field[o + 4 + ch] = t.data[4 * (n + q) + ch]; }
      }
    }
    ways = prepWays(field, w.W1, w.H1, B.d.look, B.d.marks, { pal: B.d.pal.palette, ids: B.ids, noneId, treelineM: B.bands.treeline_m }, { x0: w.x0, z0: w.z0, w: w.w, h: w.h });
  }
  // WAYS-Q4: the 4 m mass window (Q4.B bytes a cell, EMPTY = rmin 255, no entry), prepQ4 on it
  if (G.q4) {
    const w = G.q4, n = s.ways.q4.px, cells = new Uint8Array(w.w * w.h * Q4.B);
    for (let q = 0; q < w.w * w.h; q++) cells[Q4.B * q + 8] = 255;
    for (let ty = Math.floor(w.z0 / n); ty <= Math.floor((w.z0 + w.h - 1) / n); ty++) for (let tx = Math.floor(w.x0 / n); tx <= Math.floor((w.x0 + w.w - 1) / n); tx++) {
      const t = got.get(`q:${tx}_${ty}`);
      if (!t) continue;
      const tc = q4FromPlanes(t.data, n);
      for (let Z = Math.max(w.z0, ty * n); Z < Math.min(w.z0 + w.h, (ty + 1) * n); Z++) {
        const X0 = Math.max(w.x0, tx * n), X1 = Math.min(w.x0 + w.w, (tx + 1) * n), o = (Z - ty * n) * n - tx * n;
        cells.set(tc.subarray(Q4.B * (o + X0), Q4.B * (o + X1)), Q4.B * ((Z - w.z0) * w.w + X0 - w.x0));
      }
    }
    ways = prepQ4(cells, w.QW, w.QH, B.d.look, { pal: B.d.pal.palette, ids: B.ids, noneId, treelineM: B.bands.treeline_m }, { x0: w.x0, z0: w.z0, w: w.w, h: w.h });
  }
  const ground = new Map(), trees = [], shrubs = [];
  for (const [id, v] of got) {
    if (id.startsWith('n:')) ground.set(id.slice(2), v.mesh || v);
    else if (id.startsWith('t:')) trees.push(v);
    else if (id.startsWith('s:')) shrubs.push(v);
  }
  // SHD2: the picked level of each channel, its window (255 where no tile decoded)
  const shd2 = {}, px = B.geo.shd2.px;
  for (const ch of ['vis', 'sky']) {
    const w = G.shd[ch], L = w.L, a = new Uint8Array(w.w * w.h).fill(255), cs = String(L.cell);
    for (let gz = Math.floor(w.z0 / px); gz <= Math.floor((w.z0 + w.h - 1) / px); gz++) for (let gx = Math.floor(w.x0 / px); gx <= Math.floor((w.x0 + w.w - 1) / px); gx++) {
      const t = got.get(`v:${ch}:${cs}:${gx}_${gz}`);
      if (!t) continue;
      for (let r = 0; r < t.h; r++) {
        const Z = gz * px + r;
        if (Z < w.z0 || Z >= w.z0 + w.h) continue;
        for (let i = 0; i < t.w; i++) { const X = gx * px + i; if (X >= w.x0 && X < w.x0 + w.w) a[(Z - w.z0) * w.w + X - w.x0] = t.data[r * t.w + i]; }
      }
    }
    shd2[ch] = [{ cell: L.cell, w: L.w, h: L.h, a, win: { x0: w.x0, z0: w.z0, w: w.w, h: w.h } }];
  }
  return slotPart(B.W0, { cm, cmWin, sheet, ways, ground, shd2, trees, shrubs });
}

